import { useState } from 'react'
import { useForge } from '../data/useForge'
import { useI18n } from '../i18n/useI18n'
import { Icon } from '../components/Icon'
import { GalleryArt } from '../components/GalleryArt'
import { Layout } from '../components/Layout'

export default function Gallery() {
  const { t, lang } = useI18n()
  const { registry } = useForge()
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const gallery = registry?.gallery ?? []

  const handleCopy = async (itemId: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(itemId)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      // ignore
    }
  }

  const aspectClass = (aspect: string) => {
    switch (aspect) {
      case 'portrait':
        return 'h-96'
      case 'square':
        return 'h-64'
      default:
        return 'h-56'
    }
  }

  return (
    <Layout>
      <div className="px-4 py-12 md:px-margin-desktop">
        <div className="mb-12 max-w-3xl">
          <h1 className="font-display-lg text-display-lg text-on-surface">{t.gallery.title}</h1>
          <p className="mt-4 font-body-lg text-body-lg text-on-surface-variant">{t.gallery.subtitle}</p>
        </div>

        <div className="masonry-grid w-full">
          {gallery.map((item) => {
            const skill = registry?.skills.find((s) => s.id === item.skillId)
            return (
              <div
                key={item.id}
                className="masonry-item overflow-hidden rounded-lg border border-outline-variant bg-white shadow-forge-sm"
              >
                <div className={`relative w-full ${aspectClass(item.aspect)} bg-surface-container`}>
                  <GalleryArt id={item.id} className="absolute inset-0" />
                  <div className="absolute inset-0 flex items-end bg-gradient-to-t from-on-background/60 to-transparent p-4 opacity-0 transition-opacity duration-300 hover:opacity-100">
                    <button
                      type="button"
                      onClick={() =>
                        handleCopy(item.id, lang === 'zh' ? item.promptZh : item.prompt)
                      }
                      className="flex items-center gap-2 rounded bg-primary px-4 py-2 font-label-sm text-label-sm text-on-primary shadow-forge-sm transition-transform active:scale-[0.98]"
                    >
                      <Icon name="content_copy" size={16} />
                      {copiedId === item.id ? t.gallery.copied : t.gallery.copyPrompt}
                    </button>
                  </div>
                </div>

                <div className="p-card-padding"
                >
                  <h3 className="mb-2 font-headline-md text-body-lg font-bold text-on-surface"
                  >
                    {lang === 'zh' ? item.titleZh : item.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2"
                  >
                    <span className="rounded bg-surface-container-high px-2 py-1 font-label-sm text-label-sm text-on-surface-variant"
                    >
                      {t.gallery.skill}: {skill ? (lang === 'zh' ? skill.nameZh : skill.name) : item.skillId}
                    </span>
                  </div>
                  <p className="mt-3 font-label-sm text-label-sm text-on-surface-variant"
                  >
                    {lang === 'zh' ? item.mockNoteZh : item.mockNote}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Layout>
  )
}
